import axios from 'axios';
import { LoadingService } from '../helpers/loading';
import { AuthenticationService } from '../services/authService';
import moment from 'moment-timezone';

const BaseURL = process.env.API_ENDPOINT;
const REQUEST_TIMEOUT = 150000; // 150 seconds = 2.5 minutes

export const axiosInstance = axios.create({
  baseURL: `${BaseURL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REQUEST_TIMEOUT,
});

const redirectToHome = () => {
  window.open('/', '_self');
};

// Helper function to get timezone
const getTimezone = () => {
  try {
    // Try to use moment-timezone if available
    if (moment && moment.tz) {
      return moment.tz.guess();
    }
    // Fallback to Intl API
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // Final fallback
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }
};

axiosInstance.interceptors.request.use(
  async (request) => {
    try {
      // Skip loading if X-Skip-Loading header is present
      if (!request.headers['X-Skip-Loading']) {
        const id = LoadingService.generateId();
        request._loadingId = id; // Store the ID in the request object
        LoadingService.increment(request?.url, id);
      }

      // Add timezone header
      const timezone = getTimezone();
      request.headers['X-Timezone'] = timezone;

      let currentUser = AuthenticationService.instance().getCurrentUser();
      if (currentUser) {
        await AuthenticationService.instance().waitForAuthReady();
        currentUser = AuthenticationService.instance().getCurrentUser();
        request.headers.Authorization = `Bearer ${currentUser.token}`;
      }
      return request;
    } catch (error) {
      return request;
    }
  },
  (error) => {
    //console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Only decrement if we didn't skip loading
    if (!response?.config?.headers['X-Skip-Loading']) {
      LoadingService.decrement(response?.config?.url, response?.config?._loadingId);
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config;
    // Only decrement if we didn't skip loading
    if (!originalRequest?.headers['X-Skip-Loading']) {
      LoadingService.decrement(originalRequest?.url, originalRequest?._loadingId);
    }
    // Handle 401 error and not retried request
    if (error.response?.status === 401) {
      const currentUser = AuthenticationService.instance().getCurrentUser();
      if (currentUser && currentUser.token) {
        window.alert('Your session has expired. Please log in again.');
        AuthenticationService.instance().forceLogout();
        return;
      }
    }

    // Redirect to home
    if (error.response?.status === 403) {
      redirectToHome();
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
